import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ApiClient } from "@/pages/admin/apikeys/hooks/useApiKeys";

interface ApiClientModalProps {
  open: boolean;
  initial: ApiClient | null;
  onClose: () => void;
  onSubmit: (input: {
    name: string;
    ownerName: string;
    contactEmail: string;
    description: string;
  }) => Promise<void>;
  notify: (text: string, tone?: "success" | "error") => void;
}

// 新建 / 编辑对接方：收集名称、负责人、联系邮箱与用途说明。
export default function ApiClientModal({ open, initial, onClose, onSubmit, notify }: ApiClientModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? "");
    setOwnerName(initial?.owner_name ?? "");
    setContactEmail(initial?.contact_email ?? "");
    setDescription(initial?.description ?? "");
    setError(null);
  }, [open, initial]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError(t("apikeys.nameRequired"));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        name: name.trim(),
        ownerName: ownerName.trim(),
        contactEmail: contactEmail.trim(),
        description: description.trim(),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : t("apikeys.actionFailed");
      setError(message);
      notify(message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-foreground-950/40 px-4">
      <div className="w-full max-w-lg rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-heading text-base font-semibold text-foreground-950">
            {initial ? t("apikeys.editClientTitle") : t("apikeys.newClientTitle")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100"
            aria-label={t("apikeys.cancel")}
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="mt-4 flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-foreground-700">{t("apikeys.fieldName")} *</span>
            <input
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t("apikeys.fieldNamePlaceholder")}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-foreground-700">{t("apikeys.fieldOwner")}</span>
              <input
                type="text"
                value={ownerName}
                onChange={(event) => setOwnerName(event.target.value)}
                placeholder={t("apikeys.fieldOwnerPlaceholder")}
                className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-foreground-700">{t("apikeys.fieldEmail")}</span>
              <input
                type="text"
                value={contactEmail}
                onChange={(event) => setContactEmail(event.target.value)}
                placeholder={t("apikeys.fieldEmailPlaceholder")}
                className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </label>
          </div>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-foreground-700">{t("apikeys.fieldDesc")}</span>
            <textarea
              value={description}
              maxLength={300}
              rows={3}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={t("apikeys.fieldDescPlaceholder")}
              className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
          </label>
        </div>

        {error && (
          <p className="mt-3 flex items-center gap-1.5 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-xs text-accent-800">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-error-warning-line" />
            </span>
            {error}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            {t("apikeys.cancel")}
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => void handleSubmit()}
            className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? t("apikeys.saving") : initial ? t("apikeys.save") : t("apikeys.create")}
          </button>
        </div>
      </div>
    </div>
  );
}